import { STATUS } from "@/constants/statuses";
import { applyTransaction } from "@datorama/akita";
import ApiClientInterface from "@lib/ApiClient/ApiClientInterface";
import { Observable } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { create } from "superstruct";
import { injectable } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import ReviewsListInfoEntity from "../entities/ReviewsListInfoEntity";
import ReviewsListInfoSchema from "../schemas/ReviewsListInfoSchema";
import ReviewsListInfoStoreBase from "../stores/ReviewsListInfoStoreBase";

type ReviewListInfoParams = {
    productId?: number;
    onlySite?: boolean;
    productsOnly?: boolean;
};

@injectable()
class ReviewsListInfoServiceBase {
    constructor(
        private readonly reviewsListInfoStore: ReviewsListInfoStoreBase,
        private readonly authApiClient: ApiClientInterface,
        private readonly logger: LoggerInterface
    ) {}

    getReviewsListInfo(
        params?: ReviewListInfoParams
    ): Observable<ReviewsListInfoEntity> {
        this.logger.debug("ReviewsListInfoService.getReviewsInfo is started");
        this.reviewsListInfoStore.setStatus(STATUS.INITIAL_LOADING);
        return this.authApiClient
            .get({
                url: "/review/getReviewListInfo",
                responseType: "json",
                queryParams: params,
            })
            .pipe(
                map((v) => create(v.body, ReviewsListInfoSchema)),
                catchError((error: unknown) => {
                    this.logger.debug(
                        "ReviewsListInfoService.getReviewsInfo is errored",
                        error
                    );
                    applyTransaction(() => {
                        this.reviewsListInfoStore.setError(error as Error);
                        this.reviewsListInfoStore.setStatus(STATUS.ERROR);
                    });
                    throw error;
                }),
                tap((info) => {
                    this.logger.debug(
                        "ReviewsListInfoService.getReviewsInfo is ready"
                    );
                    applyTransaction(() => {
                        this.reviewsListInfoStore.update((state) => ({
                            ...state,
                            ...info,
                        }));
                        this.reviewsListInfoStore.setStatus(STATUS.READY);
                    });
                })
            );
    }
}

export default ReviewsListInfoServiceBase;
